import React, { useMemo } from "react";
import { Box, Typography, TextField, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AgentsContainer, AgentsHeader, AgentsSearchContainer, AgentsContent } from "../../styles";
import { useAgents } from "../../hooks/useAgents";
import { useAvailableAgents } from "../../hooks/useAvailableAgents";
import AgentCard from "./AgentCard";
import Pagination from "./Pagination";
import SearchInput from "../shared/SearchInput";
import { PlusSmall, DefaultAgent, Search } from "../../../Generic/Icons";
import { LockIcon } from "../../../Icons";
import { dumontColors } from "../../colors";

export interface AgentsViewProps {
  onOpenAgentChat: (agentId: string) => void;
  isFullscreen?: boolean;
  selectedAgentId?: string | null;
}

export default function AgentsView({ onOpenAgentChat, isFullscreen, selectedAgentId }: AgentsViewProps) {
  const { t: translate } = useTranslation();
  
  const { agents: availableAgents, isLoading: isLoadingAgents } = useAvailableAgents();
  
  const { query, setSearchQuery, currentPage, setCurrentPage, paginated, totalPages } = useAgents(availableAgents);

  const sortedAgents = useMemo(() => {
    if (!selectedAgentId) return paginated;
    return [...paginated].sort((a, b) => {
      if (a.id === selectedAgentId) return -1;
      if (b.id === selectedAgentId) return 1;
      return 0;
    });
  }, [paginated, selectedAgentId]);

  const renderAgentIcon = (iconType: string) => {
    switch (iconType) {
      default:
        return <DefaultAgent width={20} height={20} color="#00A49F" />;
    }
  };

  if (isLoadingAgents) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          padding: "48px 24px",
        }}
      >
        <CircularProgress size={32} sx={{ color: "#00A49F" }} />
        <Typography sx={{ fontSize: 14, color: dumontColors.textTertiary, mt: 2, fontFamily: "Manrope" }}>
          {translate("dumontLoadingAgents")}
        </Typography>
      </Box>
    );
  }

  if (isFullscreen) {
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: "1152px",
          margin: "24px auto 0",
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 24px)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            height: "63px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
            borderBottom: "1px solid #EBEBEB",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#1D2939", fontFamily: "Manrope" }}>
              {translate("dumontAgentsTitle")}
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 400, fontStyle: "italic", color: dumontColors.textTertiary, fontFamily: "Manrope" }}>
              {translate("dumontComingSoon")}
            </Typography>
          </Box>
          <Box
            sx={{
              height: "30px",
              padding: "0 16px",
              backgroundColor: "#00A49F",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#00918D" },
            }}
          >
            <PlusSmall width={14} height={14} color="#FFFFFF" />
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: "#FFFFFF", fontFamily: "Manrope" }}>
              {translate("dumontCreateAgent")}
            </Typography>
          </Box>
        </Box>

        {/* Search */}
        <Box
          sx={{
            padding: "12px 24px",
            borderBottom: "1px solid #EBEBEB",
          }}
        >
          <Box sx={{ position: "relative", width: "100%", height: "38px" }}>
            <TextField
              fullWidth
              placeholder={translate("dumontSearchAgents")}
              value={query}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                height: "38px",
                "& .MuiOutlinedInput-root": {
                  height: "38px",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "6px",
                  fontFamily: "Manrope",
                  fontSize: "13px",
                  fontWeight: 400,
                  color: dumontColors.textTertiary,
                  padding: "8px 36px",
                  "& fieldset": {
                    borderColor: "#E5E7EB",
                    borderWidth: "1px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#E5E7EB",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#00A49F",
                    borderWidth: "1px",
                  },
                  "& input": {
                    padding: 0,
                    height: "100%",
                    fontFamily: "Manrope",
                    fontSize: "13px",
                    fontWeight: 400,
                    color: dumontColors.textTertiary,
                    "&::placeholder": {
                      color: dumontColors.textTertiary,
                      opacity: 1,
                    },
                  },
                },
              }}
            />
            <Box
              sx={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Search width={16} height={16} color={dumontColors.textTertiary} />
            </Box>
          </Box>
        </Box>

        {/* Content - Agent Cards */}
        <Box sx={{ flex: 1, padding: "70px 24px 24px" }}>
          {sortedAgents.length === 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 24px", textAlign: "center" }}>
              <Typography sx={{ fontSize: 16, fontWeight: 500, color: dumontColors.textTertiary, mb: 1, fontFamily: "Manrope" }}>
                {query ? translate("dumontNoAgentsFound") : translate("dumontNoAgentsAvailable")}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#98A2B3", fontFamily: "Manrope" }}>
                {query ? translate("dumontTryDifferentSearchTerm") : translate("dumontAgentsAvailableSoon")}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                gap: "12px",
                width: "100%",
                flexWrap: "wrap",
              }}
            >
              {sortedAgents.map((agent) => {
                const isSelected = agent.id === selectedAgentId;
                return (
                  <Box
                    key={agent.id}
                    sx={{
                      position: "relative",
                      flex: "1 1 calc(33.333% - 8px)",
                      minWidth: "280px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      padding: "16px 0 16px 16px",
                      border: isSelected ? `2px solid ${dumontColors.primary}` : "1px solid #E5E7EB",
                      borderRadius: "8px",
                      cursor: "default",
                    }}
                  >
                    {isSelected && (
                      <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                        <LockIcon sx={{ fontSize: 16, color: dumontColors.primary }} />
                      </Box>
                    )}
                    {/* Icon */}
                    <Box
                      sx={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: "rgba(0, 164, 159, 0.1)",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {renderAgentIcon(agent.icon)}
                    </Box>
                    {/* Content */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", width: "100%", pr: "16px" }}>
                      <Box sx={{ display: "flex", alignItems: "stretch" }}>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#00A49F",
                            fontFamily: "Manrope",
                            lineHeight: 1.4,
                            flex: 1,
                          }}
                        >
                          {agent.name}
                        </Typography>
                      </Box>
                      <Box sx={{ width: "100%", height: "16.8px" }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 400,
                            color: dumontColors.textTertiary,
                            fontFamily: "Manrope",
                            lineHeight: 1.4,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {agent.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // Layout sidebar padrão
  return (
    <AgentsContainer>
      <AgentsHeader>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#1D2939" }}>{translate("dumontAgentsTitle")}</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 400, fontStyle: "italic", color: dumontColors.textTertiary, fontFamily: "Manrope" }}>
            {translate("dumontComingSoon")}
          </Typography>
        </Box>
      </AgentsHeader>

      {/* Coming soon */}
      {/* <AgentsSearchContainer>
        <SearchInput placeholder={translate("dumontSearchAgents")} value={query} onChange={(e) => setSearchQuery(e.target.value)} />
      </AgentsSearchContainer>

      <AgentsContent>
        {sortedAgents.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 24px", textAlign: "center" }}>
            <Typography sx={{ fontSize: 16, fontWeight: 500, color: dumontColors.textTertiary, mb: 1 }}>
              {query ? translate("dumontNoAgentsFound") : translate("dumontNoAgentsAvailable")}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#98A2B3" }}>
              {query ? translate("dumontTryDifferentSearchTerm") : translate("dumontAgentsAvailableSoon")}
            </Typography>
          </Box>
        ) : (
          <>
            {sortedAgents.map((agent) => (
              <AgentCard key={agent.id} agent={agent} isSelected={agent.id === selectedAgentId} />
            ))}
            <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} />
          </>
        )}
      </AgentsContent> */}
    </AgentsContainer>
  );
}
